import { Link, useNavigate } from "@tanstack/react-router";
import { authClient, queryClient } from "../api.ts";
import type { Perfil } from "../tipos.ts";
import { Barras, Logo } from "./Barras.tsx";

/**
 * Componente `Sidebar` del mockup (3:3): "Punto verde = proceso donde el usuario puede
 * cargar/modificar; punto gris = solo consulta". Reemplaza el amarillo/azul de CSGestión.
 *
 * Dos anchos: desplegado (232 px, nombres completos) y plegado (64 px, código del proceso y
 * punto de acceso; el nombre aparece como tooltip). La marca: logo SMG en blanco sobre marino
 * profundo; plegado, quedan solo las tres barras.
 */
export function Sidebar({
  perfil,
  plegado,
  onAlternarPlegado,
  onCerrarMovil,
}: {
  perfil: Perfil;
  plegado: boolean;
  onAlternarPlegado: () => void;
  onCerrarMovil: () => void;
}) {
  const navigate = useNavigate();
  const propio = perfil.procesos.filter((p) => p.esDueno);
  const apoyo = perfil.procesos.filter((p) => !p.esDueno && p.rol === "carga");
  const consulta = perfil.procesos.filter((p) => p.rol === "consulta");
  const veAuditoria =
    perfil.usuario.rolGlobal === "gerencia" || perfil.usuario.rolGlobal === "administrador";
  const esSoporte = perfil.usuario.rolGlobal === "soporte";
  const esAdministrador = perfil.usuario.rolGlobal === "administrador";

  async function salir() {
    await authClient.signOut();
    queryClient.clear();
    navigate({ to: "/login" });
  }

  return (
    <aside
      className={`flex h-full shrink-0 flex-col bg-marino-profundo text-sidebar-texto transition-[width] duration-200 ${
        plegado ? "w-[232px] lg:w-16" : "w-[232px]"
      }`}
    >
      {/* Marca */}
      <div
        className={`flex items-center pt-5 pb-4 ${plegado ? "px-5 lg:justify-center lg:px-0" : "px-5"}`}
      >
        <div className={plegado ? "lg:hidden" : ""}>
          <Logo variante="blanco" alto={34} />
          <div className="mt-2.5 flex items-center gap-2">
            <span className="text-rojo-marca">
              <Barras alto={9} />
            </span>
            <span className="font-display text-[11px] font-semibold tracking-[.14em] text-sidebar-grupo uppercase">
              Gestión Documental
            </span>
          </div>
        </div>
        {plegado && (
          <span
            className="hidden text-rojo-marca lg:block"
            title="SMG Comercial · Gestión Documental"
          >
            <Barras alto={20} />
          </span>
        )}
        <button
          type="button"
          onClick={onCerrarMovil}
          aria-label="Cerrar menú"
          className="ml-auto grid size-8 place-items-center rounded-md hover:bg-white/10 lg:hidden"
        >
          <IconoCerrar />
        </button>
      </div>

      <nav className={`flex-1 space-y-5 overflow-y-auto ${plegado ? "px-3 lg:px-2" : "px-3"}`}>
        {propio.length > 0 && <Grupo titulo="Mi proceso" items={propio} plegado={plegado} />}
        {(perfil.diligencias.total > 0 || perfil.diligencias.porVerificar > 0) && (
          <div>
            <Encabezado plegado={plegado}>Del día</Encabezado>
            <Item
              to="/diligencias"
              contador={perfil.diligencias.pendientes + perfil.diligencias.porVerificar}
              plegado={plegado}
              icono={<IconoDiligencias />}
              nombre="Diligencias"
            />
          </div>
        )}
        {apoyo.length > 0 && <Grupo titulo="Procesos de apoyo" items={apoyo} plegado={plegado} />}
        {consulta.length > 0 && <Grupo titulo="Consulta" items={consulta} plegado={plegado} />}

        <div>
          <Encabezado plegado={plegado}>Sistema</Encabezado>
          <Item
            to="/pendientes"
            contador={perfil.pendientesDeFirma}
            plegado={plegado}
            icono={<IconoFirma />}
            nombre="Pendientes de firma"
          />
          <Item
            to="/lecturas"
            contador={perfil.acusesPendientes}
            plegado={plegado}
            icono={<IconoLectura />}
            nombre="Por leer"
          />
          <Item
            to="/encuestas"
            contador={perfil.encuestasPendientes}
            plegado={plegado}
            icono={<IconoEncuesta />}
            nombre="Encuestas"
          />
          <Item to="/quices" plegado={plegado} icono={<IconoQuiz />} nombre="Reto SMG" />
          <Item
            to="/alertas"
            contador={perfil.alertasVencidas}
            plegado={plegado}
            icono={<IconoAlerta />}
            nombre="Alertas"
          />
          <Item
            to="/avisos"
            contador={perfil.notificacionesSinLeer}
            plegado={plegado}
            icono={<IconoCampana />}
            nombre="Avisos"
          />
          {veAuditoria && (
            <Item
              to="/auditoria"
              plegado={plegado}
              icono={<IconoAuditoria />}
              nombre="Reportes de auditoría"
            />
          )}
          {esAdministrador && (
            <>
              <Item to="/usuarios" plegado={plegado} icono={<IconoUsuarios />} nombre="Usuarios" />
              <Item
                to="/catalogos"
                plegado={plegado}
                icono={<IconoCatalogos />}
                nombre="Procesos, tipos y puestos"
              />
            </>
          )}
          {esSoporte && (
            <Item
              to="/firmas"
              plegado={plegado}
              icono={<IconoFirma />}
              nombre="Firmas del personal"
            />
          )}
        </div>
      </nav>

      {/* Usuario */}
      <div
        className={`flex items-center gap-3 border-t border-white/10 py-3 ${
          plegado ? "px-4 lg:flex-col lg:gap-2 lg:px-0" : "px-4"
        }`}
      >
        <div
          className="grid size-8 shrink-0 place-items-center rounded-full bg-acceso text-[11px] font-semibold text-marino-profundo"
          title={`${perfil.usuario.nombre} · ${perfil.usuario.puesto}`}
        >
          {perfil.usuario.iniciales}
        </div>
        <Link
          to="/cuenta"
          title="Mi cuenta"
          className={`min-w-0 flex-1 rounded-md hover:bg-white/10 ${plegado ? "lg:hidden" : ""}`}
          activeProps={{ className: "bg-white/[.12]" }}
        >
          <div className="truncate px-1 text-[12.5px] font-semibold text-white">
            {perfil.usuario.nombre}
          </div>
          <div className="truncate px-1 text-[11px] text-sidebar-grupo">
            {perfil.usuario.puesto}
          </div>
        </Link>
        <button
          type="button"
          onClick={salir}
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
          className="grid size-8 shrink-0 place-items-center rounded-md text-sidebar-grupo hover:bg-white/10 hover:text-white"
        >
          <IconoSalir />
        </button>
      </div>

      {/* Plegar / desplegar, solo escritorio */}
      <button
        type="button"
        onClick={onAlternarPlegado}
        title={plegado ? "Desplegar menú" : "Plegar menú"}
        aria-label={plegado ? "Desplegar menú" : "Plegar menú"}
        className="hidden items-center justify-center gap-2 border-t border-white/10 py-2.5 text-[11px] font-semibold text-sidebar-grupo hover:bg-white/10 hover:text-white lg:flex"
      >
        <span className={`transition-transform ${plegado ? "rotate-180" : ""}`}>
          <IconoChevron />
        </span>
        {!plegado && <span>Plegar</span>}
      </button>
    </aside>
  );
}

function Grupo({
  titulo,
  items,
  plegado,
}: {
  titulo: string;
  items: Perfil["procesos"];
  plegado: boolean;
}) {
  return (
    <div>
      <Encabezado plegado={plegado}>{titulo}</Encabezado>
      {items.map((p) => (
        <Item
          key={p.id}
          to="/procesos/$codigo"
          params={{ codigo: p.codigo }}
          contador={p.documentos}
          punto={p.rol === "carga" ? "verde" : "gris"}
          plegado={plegado}
          codigo={p.codigo}
          nombre={p.nombre}
        />
      ))}
    </div>
  );
}

function Encabezado({ children, plegado }: { children: React.ReactNode; plegado: boolean }) {
  return (
    <div
      className={`pb-1.5 font-display text-[10.5px] font-semibold tracking-[.12em] text-sidebar-grupo uppercase ${
        plegado ? "px-3 lg:px-0 lg:text-center lg:text-[9px] lg:tracking-normal" : "px-3"
      }`}
      title={plegado ? String(children) : undefined}
    >
      <span className={plegado ? "lg:hidden" : ""}>{children}</span>
      {plegado && <span className="hidden lg:inline">···</span>}
    </div>
  );
}

function Item({
  to,
  params,
  contador,
  punto,
  plegado,
  codigo,
  nombre,
  icono,
}: {
  to:
    | "/procesos/$codigo"
    | "/diligencias"
    | "/pendientes"
    | "/lecturas"
    | "/encuestas"
    | "/quices"
    | "/alertas"
    | "/avisos"
    | "/auditoria"
    | "/firmas"
    | "/usuarios"
    | "/catalogos";
  params?: { codigo: string };
  contador?: number;
  punto?: "verde" | "gris";
  plegado: boolean;
  /** Lo que se muestra plegado en vez del nombre (código del proceso). */
  codigo?: string;
  nombre: string;
  icono?: React.ReactNode;
}) {
  const conContador = contador !== undefined && contador > 0;
  return (
    <Link
      to={to}
      params={params ?? {}}
      title={plegado ? `${nombre}${conContador ? ` (${contador})` : ""}` : undefined}
      className={`group relative flex items-center rounded-[6px] border-l-2 border-transparent py-2 text-[13.5px] font-semibold hover:bg-white/10 ${
        plegado ? "gap-2.5 px-3 lg:justify-center lg:gap-0 lg:px-0" : "gap-2.5 px-3"
      }`}
      activeProps={{ className: "border-rojo-marca bg-white/[.12] text-white" }}
    >
      {punto && (
        <span
          className={`size-2 shrink-0 rounded-full ${punto === "verde" ? "bg-acceso" : "bg-white/35"} ${
            plegado ? "lg:absolute lg:top-1.5 lg:right-1.5" : ""
          }`}
        />
      )}
      {icono && <span className={plegado ? "hidden lg:block" : "hidden"}>{icono}</span>}
      <span className={`flex-1 truncate ${plegado ? "lg:hidden" : ""}`}>{nombre}</span>
      {plegado && codigo && (
        <span className="hidden font-display text-[11px] tracking-wide lg:block">{codigo}</span>
      )}
      {conContador && (
        <span
          className={`rounded-full bg-white/[.14] px-2 py-0.5 text-[11px] font-semibold text-white ${
            plegado
              ? "lg:absolute lg:-top-1 lg:-right-0.5 lg:min-w-4 lg:px-1 lg:py-0 lg:text-center lg:text-[9.5px] lg:leading-4"
              : ""
          }`}
        >
          {contador}
        </span>
      )}
    </Link>
  );
}

// --- iconos (trazo simple, 18 px) ---------------------------------------------------------

function IconoChevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M10 3 5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconoCerrar() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 3l10 10M13 3 3 13"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconoSalir() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 2H3v12h3M10 5l3 3-3 3M13 8H6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconoDiligencias() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="3" y="2.5" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6.5 2.5v-1h5v1" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path
        d="M6 9.5l2 2 4-4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconoFirma() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M2 14c3-1 4-5 5-7s2 1 1 3 3-1 4 0 2-1 4-1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M2 16.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconoLectura() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9 4.5C7.5 3.4 5.4 3 3 3v10.5c2.4 0 4.5.4 6 1.5 1.5-1.1 3.6-1.5 6-1.5V3c-2.4 0-4.5.4-6 1.5Zm0 0V15"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconoEncuesta() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M3 3.5h12v8.5H8l-3 3v-3H3z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M6 9.5V8M9 9.5V6M12 9.5V7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconoQuiz() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M7 7.2a2 2 0 1 1 3 1.7c-.7.4-1 .9-1 1.6M9 12.8v.1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconoAlerta() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 5.5V9l2.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function IconoCampana() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M4.5 7.5a4.5 4.5 0 0 1 9 0c0 3.5 1.5 4.5 1.5 4.5H3s1.5-1 1.5-4.5ZM7.5 14.5a1.6 1.6 0 0 0 3 0"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconoUsuarios() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="7" cy="6" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M1.5 15c.5-3 2.8-4.5 5.5-4.5s5 1.5 5.5 4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M12.5 3.5a2.5 2.5 0 0 1 0 5M14 10.8c1.5.6 2.4 1.9 2.6 3.7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconoCatalogos() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="2" y="2.5" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="10" y="2.5" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="2" y="10.5" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M13 10.5v5M10.5 13h5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconoAuditoria() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="3" y="2" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 6h6M6 9h6M6 12h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
